import { db, type OutboxEntry } from './database';
import { createUuid } from '../../../shared/lib/uuid';

export interface EnqueueChangeInput {
  entity_type: OutboxEntry['entity_type'];
  entity_id: string;
  deleted?: boolean;
  payload?: Record<string, unknown> | null;
  /** Должен совпадать с updated_at сущности в Dexie (LWW). */
  updated_at?: string;
}

export async function enqueueChange(input: EnqueueChangeInput): Promise<OutboxEntry> {
  const now = new Date().toISOString();
  const updatedAt = input.updated_at ?? now;
  const entry: OutboxEntry = {
    client_op_id: createUuid(),
    entity_type: input.entity_type,
    entity_id: input.entity_id,
    updated_at: updatedAt,
    deleted: input.deleted === true,
    payload: input.payload ?? null,
    created_at: now,
    attempts: 0,
    last_error: null,
    status: 'pending',
  };

  await db.outbox.put(entry);
  return entry;
}

export async function listOutboxBatch(limit: number): Promise<OutboxEntry[]> {
  const pending = await db.outbox.where('status').equals('pending').toArray();
  pending.sort((a, b) => a.created_at.localeCompare(b.created_at) || a.client_op_id.localeCompare(b.client_op_id));
  return pending.slice(0, limit);
}

export async function countUnsynced(): Promise<number> {
  return db.outbox.count();
}

export async function markOutboxInflight(clientOpIds: string[]): Promise<void> {
  if (clientOpIds.length === 0) {
    return;
  }
  await db.outbox
    .where('client_op_id')
    .anyOf(clientOpIds)
    .modify({ status: 'inflight' });
}

export async function markOutboxPending(clientOpIds: string[], error: string | null): Promise<void> {
  if (clientOpIds.length === 0) {
    return;
  }
  await db.outbox
    .where('client_op_id')
    .anyOf(clientOpIds)
    .modify((entry) => {
      entry.status = 'pending';
      entry.attempts += 1;
      entry.last_error = error;
    });
}

export async function deleteOutboxEntries(clientOpIds: string[]): Promise<void> {
  if (clientOpIds.length === 0) {
    return;
  }
  await db.outbox.where('client_op_id').anyOf(clientOpIds).delete();
}

export async function recoverStaleInflight(): Promise<void> {
  await db.outbox.where('status').equals('inflight').modify({ status: 'pending' });
}

export async function clearOutbox(): Promise<void> {
  await db.outbox.clear();
}

export async function dropOutboxForEntity(
  entityType: OutboxEntry['entity_type'],
  entityId: string,
): Promise<void> {
  await db.outbox
    .where('[entity_type+entity_id]')
    .equals([entityType, entityId])
    .delete();
}
